import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { initThirdParty } from '../lib/measure';
import { PageLoader } from '../ui';
import { SiteLayout } from './layout/SiteLayout';
import { RequireAuth, RequireHost } from './guards';

const page = (loader) => lazy(loader);

// Guest
const LandingPage = page(() => import('../features/landing/LandingPage'));
const SearchPage = page(() => import('../features/search/SearchPage'));
const HotelPage = page(() => import('../features/hotel/HotelPage'));
const DirectBookPage = page(() => import('../features/hotel/DirectBookPage'));
const ConfirmationPage = page(() => import('../features/checkout/ConfirmationPage'));
const EmailCancelPage = page(() => import('../features/bookings/EmailCancelPage'));
const BookingsPage = page(() => import('../features/bookings/BookingsPage'));

// Account
const ProfilePage = page(() => import('../features/account/ProfilePage'));
const DeleteAccountPage = page(() => import('../features/account/DeleteAccountPage'));
const ResetPasswordPage = page(() => import('../features/account/ResetPasswordPage'));
const ConfirmEmailPage = page(() => import('../features/account/ConfirmEmailPage'));
const UnsubscribePage = page(() => import('../features/account/UnsubscribePage'));

// Hosts
const HostHomePage = page(() => import('../features/host/HostHomePage'));
const PropertyFormPage = page(() => import('../features/host/PropertyFormPage'));
const RoomsPage = page(() => import('../features/host/RoomsPage'));
const PhotosPage = page(() => import('../features/host/PhotosPage'));
const ConnectCompletePage = page(() => import('../features/host/ConnectCompletePage'));
const ConnectRefreshPage = page(() => import('../features/host/ConnectRefreshPage'));
const PropertyInvitePage = page(() => import('../features/invites/PropertyInvitePage'));
const PropertyInviteAcceptPage = page(() => import('../features/invites/PropertyInviteAcceptPage'));
const RegisterHostPage = page(() => import('../features/invites/RegisterHostPage'));

// Content
const BlogPage = page(() => import('../features/content/BlogPage'));
const BlogPostPage = page(() => import('../features/content/BlogPostPage'));
const ContactPage = page(() => import('../features/content/ContactPage'));
const FaqPage = page(() => import('../features/content/FaqPage'));
const TeamPage = page(() => import('../features/content/TeamPage'));
const PressPage = page(() => import('../features/content/PressPage'));
const CareersPage = page(() => import('../features/content/CareersPage'));
const JobPage = page(() => import('../features/content/JobPage'));
const HostWithUsPage = page(() => import('../features/content/HostWithUsPage'));
const AppsPage = page(() => import('../features/content/AppsPage'));
const ContestPage = page(() => import('../features/content/ContestPage'));
const SitemapPage = page(() => import('../features/content/SitemapPage'));
const AdultsHotelsPage = page(() => import('../features/content/AdultsHotelsPage'));
const TermsPage = page(() => import('../features/legal/TermsPage'));
const PrivacyPage = page(() => import('../features/legal/PrivacyPage'));
const BookingPolicyPage = page(() => import('../features/legal/BookingPolicyPage'));
const NotFoundPage = page(() => import('../features/content/NotFoundPage'));

const authed = (element) => <RequireAuth>{element}</RequireAuth>;
const host = (element) => <RequireHost>{element}</RequireHost>;

export function App() {
  useEffect(() => {
    initThirdParty();
  }, []);

  return (
    <Suspense fallback={<PageLoader label="Loading…" minHeight="70vh" />}>
      <Routes>
        <Route element={<SiteLayout />}>
          <Route index element={<LandingPage />} />

          {/* Search: /hotels (near you), /hotels/:place (city pages), /hotels/:cc/:geoname */}
          <Route path="hotels" element={<SearchPage />} />
          <Route path="hotels/18-plus" element={<AdultsHotelsPage />} />
          <Route path="hotels/:place" element={<SearchPage />} />
          <Route path="hotels/:cc/:geoname" element={<SearchPage />} />

          {/* Hotel + booking */}
          <Route path="hotel/:id/:provider?" element={<HotelPage />} />
          <Route path="hotel/:id/:provider/:cc/:geoname/:hotelName" element={<HotelPage />} />
          <Route path="book/:hotelId/:productId" element={<DirectBookPage />} />
          <Route path="booking/confirmation/:bookingNumber" element={<ConfirmationPage />} />
          <Route path="booking/cancel" element={<EmailCancelPage />} />
          <Route path="booking/cancel/:booking_id" element={<EmailCancelPage />} />
          <Route path="booking/cancel/:token/:booking_id/:provider?" element={<EmailCancelPage />} />

          {/* Account */}
          <Route path="bookings" element={authed(<BookingsPage />)} />
          <Route path="bookings/:bookingNumber" element={authed(<BookingsPage />)} />
          <Route path="my-bookings" element={<Navigate to="/bookings" replace />} />
          <Route path="profile" element={authed(<ProfilePage />)} />
          <Route path="delete-account" element={authed(<DeleteAccountPage />)} />
          <Route path="forgot-password/:token" element={<ResetPasswordPage />} />
          <Route path="confirm-email/:token" element={<ConfirmEmailPage />} />
          <Route path="unsubscribe/:token" element={<UnsubscribePage />} />

          {/* Hosts */}
          <Route path="self-host" element={<HostHomePage />} />
          <Route path="self-host/hotel/new" element={host(<PropertyFormPage />)} />
          <Route path="self-host/hotel/:hotelId" element={host(<PropertyFormPage />)} />
          <Route path="self-host/hotel/:hotelId/rooms" element={host(<RoomsPage />)} />
          <Route path="self-host/hotel/:hotelId/images" element={host(<PhotosPage />)} />
          <Route path="host/connect/complete" element={host(<ConnectCompletePage />)} />
          <Route path="host/connect/refresh" element={host(<ConnectRefreshPage />)} />
          <Route path="property-invite" element={<PropertyInvitePage />} />
          <Route path="property-invite-accept" element={<PropertyInviteAcceptPage />} />
          <Route path="register-host" element={<RegisterHostPage />} />

          {/* Content */}
          <Route path="blog" element={<BlogPage />} />
          <Route path="blog/:id/:title?" element={<BlogPostPage />} />
          <Route path="contact-us" element={<ContactPage />} />
          <Route path="faq" element={<FaqPage />} />
          <Route path="team" element={<TeamPage />} />
          <Route path="press" element={<PressPage />} />
          <Route path="careers" element={<CareersPage />} />
          <Route path="careers/:slug" element={<JobPage />} />
          <Route path="host-with-us" element={<HostWithUsPage />} />
          <Route path="apps" element={<AppsPage />} />
          <Route path="contest" element={<ContestPage />} />
          <Route path="sitemap" element={<SitemapPage />} />
          <Route path="terms" element={<TermsPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="booking-policy" element={<BookingPolicyPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
